import React, { useCallback, useEffect, useMemo } from 'react';

import { useCurrentProjectId, useUser } from '@adsk/alloy-provider';
import { Box, Flex } from '@adsk/alloy-react-box';
import { displayDate, VALID_DATE_FORMATS } from '@adsk/alloy-react-datetime';
import FilterButton from '@adsk/alloy-react-filter-button';
import { ICircleIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import theme from '@adsk/alloy-react-theme';

import ScheduleSearchField from './ScheduleSearchField';
import { useProjectSchedules } from '../../hooks';
import { ScheduleFilters } from '../../types';
import ScheduleDropdown from '../ScheduleDropdown';
import { useScheduleContext } from '../ScheduleProvider';
import { ACTIONS } from '../ScheduleProvider/ScheduleProvider';

type TopBarProps = {
  searchExpression: string;
  onSearchChange: (term: string) => void;
  showSearchBox: boolean;
  disabled: boolean;
  filtersAvailable: boolean;
  filtersOpen: boolean;
  filters: ScheduleFilters;
  toggleFiltersPanel: () => void;
  setFilters: (filters: object) => void;
  filtersCount: number;
};

function ScheduleTopBar({
  searchExpression,
  onSearchChange,
  showSearchBox,
  disabled,
  filtersAvailable,
  filtersOpen,
  filters,
  toggleFiltersPanel,
  filtersCount,
  setFilters,
}: TopBarProps) {
  const schedules = useProjectSchedules();
  const projectId = useCurrentProjectId();
  const { user } = useUser();
  const { setSelection, setLaborResources, setActivityCodesMap, dispatch } =
    useScheduleContext();
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));

  const onChangeSchedule = useCallback(
    (scheduleId: string) => {
      setFilters({ scheduleId });
      if (setSelection) {
        setSelection([]);
      }
      setLaborResources([]);
      setActivityCodesMap(new Map());
      dispatch({ type: ACTIONS.UPDATE_HIDDEN_COLUMNS });
      dispatch({ type: ACTIONS.UPDATE_COLUMN_ORDER });
    },
    [
      setFilters,
      setSelection,
      setActivityCodesMap,
      setLaborResources,
      dispatch,
    ],
  );

  const currentSchedule = useMemo(
    () => schedules?.find((schedule) => schedule.id === filters.scheduleId),
    [schedules, filters.scheduleId],
  );

  const recentlySeenSchedule = useMemo(() => {
    if (!user?.autodeskId) return null;
    return localStorage.getItem(
      `acs-schedule-user-${user.autodeskId}-project-${projectId}-last-viewed`,
    );
  }, [user, projectId]);

  // set default schedule
  useEffect(() => {
    if (!filters.scheduleId && schedules?.length > 0) {
      onChangeSchedule(schedules[0].id);
    }
  }, [schedules, filters.scheduleId, onChangeSchedule]);

  const onClearFilters = useCallback(() => {
    setFilters({ scheduleId: filters.scheduleId });
  }, [setFilters, filters.scheduleId]);

  return (
    <Flex
      justifyContent="space-between"
      marginBottom="16px"
      alignItems="flex-start"
    >
      {currentSchedule && (
        <>
          <Box
            display="grid"
            gridAutoFlow="row"
            minWidth="unset"
            style={{ rowGap: '8px' }}
          >
            <ScheduleDropdown
              scheduleId={currentSchedule.id}
              onChangeSchedule={onChangeSchedule}
              schedules={schedules}
              recentlySeenSchedule={recentlySeenSchedule}
            />
            <Box
              display="grid"
              gridAutoFlow="column"
              alignItems="center"
              justifyContent="flex-start"
              color="charcoal700"
              fontSize="12px"
              style={{ columnGap: '4px' }}
            >
              <ICircleIcon size={16} color={theme.colors.charcoal700} />

              {currentSchedule.dataDate
                ? T.translate('DATA_DATE', {
                    date: displayDate(new Date(currentSchedule.dataDate), {
                      format: VALID_DATE_FORMATS.DEFAULT,
                    }),
                  })
                : T.translate('DATA_DATE_UNAVAILABLE')}
            </Box>
          </Box>
          <Flex alignItems="center">
            {showSearchBox && (
              <ScheduleSearchField
                searchExpression={searchExpression}
                onSearchChange={onSearchChange}
                disabled={disabled}
              />
            )}
            {filtersAvailable && (
              <FilterButton
                style={{
                  marginLeft: 12,
                }}
                filtersOpen={filtersOpen}
                onClick={toggleFiltersPanel}
                clearFilters={onClearFilters}
                filtersCount={
                  filtersCount > 0 ? filtersCount - 1 : filtersCount
                }
                disabled={disabled}
              />
            )}
          </Flex>
        </>
      )}
    </Flex>
  );
}

export default ScheduleTopBar;
